/**
 * Canonical URL → `data-amp-track-context` registry.
 *
 * Source of truth: `registry/amplitude-registry.json` (generated via `yarn sync:amp-registry`).
 * The values here MUST match the cross-team "STANDARD NAMES REGISTRY" so analytics
 * dashboards stay consistent.
 */

import {
  AMP_TRACK_CONTEXT_DEFAULT,
  AMP_TRACK_CONTEXT_REGISTRY,
  AMP_TRACK_CONTEXT_VALUES
} from './generated/amp-track-context-registry.generated';
import type { AmpTrackContextValue } from './generated/amp-track-types.generated';

export { AMP_TRACK_CONTEXT_DEFAULT, AMP_TRACK_CONTEXT_REGISTRY, AMP_TRACK_CONTEXT_VALUES };
export type { AmpTrackContextValue };

export interface AmpTrackContextRule {
  /** Path prefix to match against `Router.url` (must start with `/`). */
  pathPrefix: string;
  /** Canonical context value emitted as `data-amp-track-context`. */
  context: AmpTrackContextValue;
}

/**
 * Strip query string and fragment, normalize trailing slash.
 * Keeps a leading `/` so all rules can use the canonical `/foo` form.
 */
export function normalizeUrlForContext(url: string | null | undefined): string {
  if (!url) return '/';
  const noQuery = url.split('?')[0].split('#')[0];
  if (noQuery === '/') return '/';
  return noQuery.replace(/\/+$/, '') || '/';
}

function matches(url: string, prefix: string): boolean {
  return url === prefix || url.startsWith(prefix + '/');
}

/**
 * Resolve the canonical `data-amp-track-context` for a given URL.
 *
 * The longest matching `pathPrefix` wins, which gives upsells precedence over
 * their parent reports (e.g. `/upgrade/screencasts` → `upsell-screencasts`,
 * not `screencasts`).
 */
export function resolveAmpTrackContextFromUrl(
  url: string | null | undefined,
  fallback: AmpTrackContextValue = AMP_TRACK_CONTEXT_DEFAULT,
  registry: readonly AmpTrackContextRule[] = AMP_TRACK_CONTEXT_REGISTRY,
): AmpTrackContextValue {
  const normalized = normalizeUrlForContext(url);

  let best: AmpTrackContextRule | null = null;
  for (const rule of registry) {
    if (!matches(normalized, rule.pathPrefix)) continue;
    if (!best || rule.pathPrefix.length > best.pathPrefix.length) {
      best = rule;
    }
  }

  return best?.context ?? fallback;
}
